<template>
  <div style="margin: 60px 0">
    <div
      style="display: flex; justify-content: space-between; align-items: center"
    >
      <div>
        <h1 style="font-size: large; font-weight: 700">
          KYC & Compliance Review
        </h1>
        <p style="color: #6b7280; font-size: 14px">
          View the Seller's KYC and Compliance Documents
        </p>
      </div>

      <div style="display: flex; align-items: center; gap: 16px">
        <a-button
          v-if="kycStatus === 'Approved'"
          :loading="exportLoading"
          @click="rejectDocuments"
          style="border-radius: 6px; background: #fef2f2; color: #b91c1c"
          icon="flag"
        >
          Cancel Approval
        </a-button>
        <a-button
          v-if="kycStatus !== 'Approved'"
          :loading="exportLoading"
          @click="approveAllDocuments"
          style="border-radius: 6px; background: #ecfdf5; color: #047857"
          icon="check"
        >
          Approve Documents
        </a-button>
      </div>
    </div>

    <div>
      <a-row
        :gutter="[16, { xs: 8, sm: 16, md: 24, lg: 32 }]"
        style="margin: 10px 0"
        type="flex"
        align="top"
      >
        <a-col :span="18">
          <div
            style="
              background: #fff;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 16px;
              min-height: 450px;
            "
          >
            <div style="padding: 10px; border-bottom: 1px solid #e5e7eb">
              <h1 style="font-size: 18px; font-weight: 600">Document Review</h1>
            </div>

            <a-table
              :columns="columns"
              :data-source="documents"
              row-key="id"
              :pagination="{ pageSize: 10 }"
              :scroll="{ x: 'max-content' }"
              :loading="loading"
              style="background-color: #fafafa"
            >
              <!-- Status Tag -->
              <template slot="status" slot-scope="text">
                <a-tag
                  :color="
                    text === 'Approved'
                      ? 'green'
                      : text === 'Pending'
                      ? 'orange'
                      : text === 'Rejected'
                      ? 'red'
                      : 'blue'
                  "
                >
                  {{ text }}
                </a-tag>
              </template>

              <!-- Actions -->
              <template slot="actions" slot-scope="text, record">
                <div class="action-buttons">
                  <a-button type="link" @click="viewDocument(record)">
                    <i
                      class="fa fa-eye"
                      style="color: #9e9e9e"
                      aria-hidden="true"
                    ></i>
                  </a-button>
                  <a-button
                    v-if="record.status !== 'Approved'"
                    type="link"
                    @click="approveSingleDocument(record)"
                  >
                    <i
                      class="fa fa-check"
                      style="color: #52c41a"
                      aria-hidden="true"
                    ></i>
                  </a-button>
                  <a-button
                    type="link"
                    @click="rejectDocument(record)"
                    :disabled="record.status === 'Rejected'"
                  >
                    <i
                      class="fa fa-times"
                      style="color: #f5222d"
                      aria-hidden="true"
                    ></i>
                  </a-button>
                </div>
              </template>
              <template slot="documentType" slot-scope="text, record">
                <a
                  @click="viewDocument(record)"
                  style="
                    color: #1890ff;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                  "
                >
                  <i
                    class="fa fa-file"
                    style="margin-right: 6px; color: #e74c3c"
                    aria-hidden="true"
                  ></i>
                  {{ text }}
                </a>
              </template>

              <template slot="uploadedBy" slot-scope="text, record">
                <div
                  v-if="documents.length"
                  style="display: flex; align-items: center"
                >
                  <a-avatar
                    :style="{
                      backgroundColor: '#1890ff',
                      verticalAlign: 'middle',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }"
                    size="small"
                    style="margin-right: 8px"
                  >
                    <span>{{ getInitials(record.uploadedBy) }}</span>
                  </a-avatar>
                  {{ text }}
                </div>
              </template>

              <template #pagination="paginationRender">
                <div style="margin-top: 40px">
                  <component :is="paginationRender" />
                </div>
              </template>
            </a-table>
          </div>
        </a-col>
        <a-col :span="6">
          <div
            style="
              display: flex;
              flex-direction: column;
              height: 100%;
              min-height: 100%;
            "
          >
            <div
              style="
                background: #f9fafb;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                padding: 16px;
                flex: 1;
                margin-bottom: 8px;
              "
            >
              <h1 style="font-size: 18px; font-weight: 600">
                Verification Timeline
              </h1>
              <div
                style="
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  margin-top: 16px;
                "
              >
                <i
                  class="fa fa-circle"
                  aria-hidden="true"
                  style="color: #3b82f6"
                ></i>

                <div>
                  <p
                    style="
                      font-weight: 600;
                      font-size: 14px;
                      padding: 0;
                      margin: 0;
                    "
                  >
                    Documents Submitted
                  </p>
                  <p
                    style="
                      color: #6b7280;
                      font-size: 12px;
                      padding: 0;
                      margin: 0;
                    "
                  >
                    {{ formatDate(data.createdAt) }}
                  </p>
                </div>
              </div>
            </div>

            <div
              style="
                background: #ffffff;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                padding: 16px;
                flex: 1;
              "
            >
              <h1 style="font-size: 18px; font-weight: 600">
                Compliance Notes
              </h1>

              <a-form
                :model="formState"
                name="basic"
                :label-col="{ span: 8 }"
                :wrapper-col="{ span: 16 }"
                autocomplete="off"
                @finish="onFinish"
                @finishFailed="onFinishFailed"
              >
                <a-textarea
                  v-model="newNote"
                  :placeholder="
                    editingNote ? 'Edit note...' : 'Add internal note...'
                  "
                  :rows="2"
                  style="margin-top: 20px"
                />
                <div style="display: flex; gap: 8px; margin-top: 10px">
                  <a-button
                    @click="submitForm"
                    style="background-color: #26a246; color: #fff; flex: 1"
                  >
                    {{ editingNote ? "Update Note" : "Add Note" }}
                  </a-button>
                  <a-button
                    v-if="editingNote"
                    @click="cancelEdit"
                    style="flex: 1"
                  >
                    Cancel
                  </a-button>
                </div>
              </a-form>

              <!-- Notes List -->
              <div
                v-for="note in notes"
                :key="note.id"
                style="margin-top: 20px"
              >
                <div
                  style="
                    background-color: #f9fafb;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 12px;
                  "
                >
                  <div
                    style="
                      display: flex;
                      align-items: flex-start;
                      justify-content: space-between;
                    "
                  >
                    <div style="display: flex; align-items: center">
                      <a-avatar
                        :style="{
                          backgroundColor: '#1890ff',
                          verticalAlign: 'middle',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }"
                        size="small"
                        style="margin-right: 8px"
                      >
                        <span>{{ getInitials(note.author) }}</span>
                      </a-avatar>
                      <p
                        style="
                          margin-bottom: 0px;
                          color: #6b7280;
                          font-size: 12px;
                        "
                      >
                        {{ note.author }}
                      </p>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px">
                      <div class="note-actions">
                        <a-dropdown>
                          <a class="ant-dropdown-link" @click.prevent>
                            <i
                              class="fa fa-ellipsis-h"
                              style="color: #6b7280; cursor: pointer"
                            ></i>
                          </a>
                          <a-menu slot="overlay">
                            <a-menu-item @click="editNote(note)">
                              <i
                                class="fa fa-pen"
                                style="margin-right: 8px; color: #6b7280"
                              ></i>
                              Edit
                            </a-menu-item>
                            <a-menu-item @click="deleteNote(note.id)">
                              <i
                                class="fa fa-trash"
                                style="margin-right: 8px; color: #6b7280"
                              ></i>
                              Delete
                            </a-menu-item>
                          </a-menu>
                        </a-dropdown>
                      </div>
                    </div>
                  </div>

                  <p
                    style="
                      color: #4b5563;
                      font-size: 12px;
                      margin-top: 10px;
                      margin-bottom: 10px;
                    "
                  >
                    {{ note.content }}
                  </p>

                  <p
                    style="
                      margin-bottom: 0px;
                      color: #6b7280;
                      font-size: 12px;
                      display: flex;
                      justify-content: flex-end;
                    "
                  >
                    {{ relativeTime(note.timestamp) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </a-col>
      </a-row>
    </div>
    <DocumentPreviewModal
      :visible="previewVisible"
      :documentUrl="previewDocumentUrl"
      @close="handlePreviewCancel"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatDate, getFileDownloadUrl, getInitials, getRelativeTime, documentApprovalPropertyMap } from "../../../utils";
import sellersService from "../../../api/sellers.service";
import { mapActions } from "vuex";
import DocumentPreviewModal from "../../shared-components/DocumentPreviewModal.vue";

export default {
  name: "KycCompliance",
  components: { DocumentPreviewModal },
  props: {
    data: {
      type: Object,
      required: true,
    },
    /**
     * Emits:
     * - status-changed: when the button is clicked
     */
  },
  data() {
    return {
      loading: false,
      exportLoading: false,
      documents: [],
      columns: [
        {
          title: "Document Type",
          dataIndex: "documentType",
          key: "documentType",
          scopedSlots: { customRender: "documentType" },
        },
        {
          title: "Uploaded By",
          dataIndex: "uploadedBy",
          key: "uploadedBy",
          scopedSlots: { customRender: "uploadedBy" },
        },
        {
          title: "Date",
          dataIndex: "date",
          key: "date",
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: "Actions",
          key: "actions",
          scopedSlots: { customRender: "actions" },
        },
      ],
      notes: [],
      newNote: "",
      formState: { note: "" },
      editingNote: null,
      previewVisible: false,
      previewDocumentUrl: "",
    };
  },
  computed: {
    ...mapGetters(["IsAgentProfileAvailable"]),
    agentProfile() {
      return this.IsAgentProfileAvailable || {};
    },
    kycStatus() {
      return this.data?.accountDetails?.sellerApprovalStatus;
    },
  },

  mounted() {
    this.initializeDocuments();
  },

  methods: {
    ...mapActions("sellerManagement", ["fetchSeller"]),
    formatDate,
    getInitials,
    initializeDocuments() {
      if (!this.data) return;
      this.documents = [
        {
          id: 1,
          documentType: "Certificate of Registration",
          uploadedBy: this.data?.sellerName || "data",
          date: formatDate(this.data?.createdAt),
          status: this.data.businessDetails?.approvalStatus,
          documentUrl: getFileDownloadUrl(this.data.businessDetails?.certificateOfRegistration?.[0] || ""),
        },
        {
          id: 2,
          documentType: "Proof of Payment",
          uploadedBy: this.data?.sellerName || "data",
          date: formatDate(this.data?.createdAt),
          status: this.data.paymentInformation?.approvalStatus,
          documentUrl: getFileDownloadUrl(this.data.paymentInformation?.proofOfDocument?.[0] || ""),
        },
        {
          id: 3,
          documentType: "National ID Card",
          uploadedBy: this.data?.sellerName || "data",
          date: formatDate(this.data?.createdAt),
          status: this.data.accountDetails?.nationalIdApprovalStatus,
          documentUrl: getFileDownloadUrl(this.data.accountDetails?.nationalIdPicture || ""),
        },
      ];
    },

    viewDocument(record) {
      if (record.documentUrl) {
        this.previewDocumentUrl = record.documentUrl;
        this.previewVisible = true;
      } else {
        this.$message.warning("Document not available");
      }
    },

    handlePreviewCancel() {
      this.previewVisible = false;
      this.previewDocumentUrl = "";
    },

    async approveSingleDocument(record) {
      this.$confirm({
        title: "Approve Document",
        content: "Are you sure you want to approve this document?",
        okText: "Yes",
        okType: "success",
        cancelText: "No",
        onOk: async () => {
          const approvalType = documentApprovalPropertyMap[record.documentType];

          const response = await sellersService.approveDocuments(
            this.data?.userSetupId,
            approvalType
          );
          if (response.data?.code === 200) {
            this.$message.success(
              `${record.documentType} has been verified successfully`
            );
            this.$emit("status-changed");
          } else {
            this.$message.error(
              response.data?.message || "Failed to verify document"
            );
          }
        },
      });
    },

    async rejectDocument(record) {
      this.$confirm({
        title: "Reject Document",
        content: "Are you sure you want to reject this document?",
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        onOk: async () => {
          const approvalType = documentApprovalPropertyMap[record.documentType] || record.documentType;

          const response = await sellersService.rejectDocuments(
            this.data?.userSetupId,
            approvalType
          );

          if (response.data?.code === 200) {
            this.$message.success(
              `${record.documentType} has been rejected successfully`
            );
            this.$emit("status-changed");
          } else {
            this.$message.error(
              response.data?.message || "Failed to reject document"
            );
          }
        },
      });
    },

    async approveAllDocuments() {
      try {
        if (!this.documents.length) {
          this.$message.warning("No documents available to approve");
          return;
        }

        this.$confirm({
          title: "Approve All Documents",
          content:
            "Are you sure you want to approve all documents? This action cannot be undone.",
          okText: "Yes, Approve All",
          okType: "success",
          cancelText: "No",
          onOk: async () => {
            const response = await sellersService.approveDocuments(
              this.data?.userSetupId,
              "ApproveAll"
            );
            if (response.data?.code === 200) {
              this.$message.success(
                "All documents have been verified successfully"
              );
              this.$emit("status-changed");
            } else {
              this.$message.error(
                response.data?.message || "Failed to approve all documents"
              );
            }
          },
        });
      } catch (error) {
        console.error("Error in approveAllDocuments:", error);
        this.$message.error("An error occurred while processing the request");
      }
    },

    async rejectDocuments() {
      this.$confirm({
        title: "Cancel Approval",
        content:
          "Are you sure you want to cancel the approval for all documents? This will mark them as pending review.",
        okText: "Yes, Cancel Approval",
        okType: "warning",
        cancelText: "No",
        onOk: async () => {
          const approvalType = "ApproveAll";
          const response = await sellersService.rejectDocuments(
            this.data?.userSetupId,
            approvalType
          );

          if (response.data?.code === 200) {
            this.$message.success("Approval of Documents has been cancelled");
            this.$emit("status-changed");
          } else {
            this.$message.error(
              response.data?.message || "Failed to cancel approval"
            );
          }
        },
      });
    },

    // formattedDate(dateString) {
    //   return formatDate(dateString);
    // },

    relativeTime(dateString) {
      return getRelativeTime(dateString);
    },

    submitForm() {
      if (!this.newNote.trim()) {
        this.$message.warning("Please enter a note");
        return;
      }

      if (this.editingNote) {
        const index = this.notes.findIndex(
          (note) => note.id === this.editingNote.id
        );
        if (index !== -1) {
          this.notes[index] = {
            ...this.editingNote,
            content: this.newNote,
            timestamp: new Date().toISOString(),
          };
          this.$message.success("Note updated successfully");
        }
        this.editingNote = null;
      } else {
        const note = {
          id: Date.now(),
          content: this.newNote,
          author: this.agentProfile?.name || "Admin User",
          timestamp: new Date().toISOString(),
        };
        this.notes.unshift(note);
        this.$message.success("Note added successfully");
      }
    },

    editNote(note) {
      this.editingNote = note;
      this.newNote = note.content;
    },

    cancelEdit() {
      this.editingNote = null;
      this.newNote = "";
    },

    deleteNote(noteId) {
      this.$confirm({
        title: "Are you sure you want to delete this note?",
        content: "This action cannot be undone.",
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        onOk: () => {
          this.notes = this.notes.filter((note) => note.id !== noteId);
          this.$message.success("Note deleted successfully");
        },
      });
    },

    onFinish(values) {
      console.log("Success:", values);
    },

    onFinishFailed(errorInfo) {
      console.log("Failed:", errorInfo);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initializeDocuments();
        }
      },
    },
  },
};
</script>

<style scoped>
.action-buttons .ant-btn {
  padding: 0 4px;
  height: 24px;
  line-height: 24px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 2px;
}

.note-actions {
  display: flex;
  align-items: center;
}

.note-actions .ant-dropdown-link {
  padding: 4px 8px;
  border-radius: 4px;
}

.note-actions .ant-dropdown-link:hover {
  background-color: #f3f4f6;
}

.ant-menu-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
}

.ant-menu-item:hover {
  background-color: #f3f4f6;
}
</style>
